import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight, List, Printer } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { DocumentPriority } from '../lib/types';
import {
  DocTypeBlock, priorityStyles, priorityRank, statusStyles,
  type DocStatus, type DocTypeRow,
} from '../components/DocTypeBlock';
import { PageLoader } from '../components/PageLoader';
import ConnectSourceButton from '../components/ConnectSourceButton';
import NextSteps from '../components/NextSteps';
import DismissableInfoCard from '../components/DismissableInfoCard';

interface IndexSubsection {
  id: string;
  name: string;
  number: string;
  documents: DocTypeRow[];
}

interface IndexSection {
  id: string;
  name: string;
  number: string;
  documents: DocTypeRow[];
  subsections: IndexSubsection[];
}

const PRIORITY_ORDER: DocumentPriority[] = ['required', 'recommended', 'optional'];
const STATUS_ORDER: Exclude<DocStatus, 'pending'>[] = ['published', 'missing', 'candidates'];

// The filters live in the query string, so a view is linkable (the Home getting-started step points at one).
const DEFAULT_PRIORITIES: DocumentPriority[] = ['required', 'recommended'];
const DEFAULT_STATUSES: DocStatus[] = ['published', 'missing', 'candidates'];

function parseFilter<T extends string>(raw: string | null, allowed: readonly T[], fallback: readonly T[]): Set<T> {
  const picked = (raw ?? '').split(',').map(s => s.trim()).filter((s): s is T => (allowed as readonly string[]).includes(s));
  return new Set(picked.length ? picked : fallback);
}

const statusRank: Record<DocStatus, number> = { missing: 0, candidates: 1, published: 2, pending: 3 };

function docVisible(
  doc: DocTypeRow,
  priorityFilter: Set<DocumentPriority>,
  statusFilter: Set<DocStatus>,
): boolean {
  // Published docs bypass the priority and pending filters but honour the status filter.
  if (doc.status === 'published') return statusFilter.has('published');
  if (doc.pending) return false;
  return priorityFilter.has(doc.priority) && statusFilter.has(doc.status);
}

function sectionMatchesFilter(
  section: IndexSection | IndexSubsection,
  priorityFilter: Set<DocumentPriority>,
  statusFilter: Set<DocStatus>,
): boolean {
  if (section.documents.some(d => docVisible(d, priorityFilter, statusFilter))) return true;
  if ('subsections' in section) {
    return section.subsections.some(s => sectionMatchesFilter(s, priorityFilter, statusFilter));
  }
  return false;
}

function SectionBlock({
  section,
  priorityFilter,
  statusFilter,
  depth = 0,
}: {
  section: IndexSubsection | IndexSection;
  priorityFilter: Set<DocumentPriority>;
  statusFilter: Set<DocStatus>;
  depth?: number;
}) {
  const subsections = 'subsections' in section ? section.subsections : [];
  const isRoot = depth === 0;

  const docs = section.documents
    .filter(d => docVisible(d, priorityFilter, statusFilter))
    .slice()
    .sort((a, b) => {
      const p = priorityRank[a.priority] - priorityRank[b.priority];
      if (p !== 0) return p;
      const s = statusRank[a.status] - statusRank[b.status];
      if (s !== 0) return s;
      return a.name.localeCompare(b.name);
    });
  const visibleSubs = subsections.filter(s => sectionMatchesFilter(s, priorityFilter, statusFilter));

  if (docs.length === 0 && visibleSubs.length === 0) return null;

  return (
    <div className={isRoot ? 'mb-6 last:mb-0' : 'mb-3 last:mb-0'}>
      <Link
        to={`/workspace/${section.id}`}
        className={`group flex items-center gap-2 mb-2 hover:text-brand-dark transition-colors ${
          isRoot ? 'text-base font-semibold text-graphite' : 'text-sm font-medium text-graphite'
        }`}
      >
        <span className="font-mono text-coolslate">{section.number}</span>
        <span>{section.name}</span>
        <ChevronRight className="w-3.5 h-3.5 text-coolslate opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>

      {docs.length > 0 && (
        <div className="space-y-1.5 ml-6 mb-3">
          {docs.map(doc => (
            <DocTypeBlock key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      {visibleSubs.length > 0 && (
        <div className="ml-4">
          {visibleSubs.map(sub => (
            <SectionBlock
              key={sub.id}
              section={sub}
              priorityFilter={priorityFilter}
              statusFilter={statusFilter}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorkspaceIndexPage() {
  const { authFetch } = useAuth();
  const { workspaceName, hasActiveJobs } = useWorkspace();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sections, setSections] = useState<IndexSection[] | null>(null);
  const priorityFilter = parseFilter(searchParams.get('priority'), PRIORITY_ORDER, DEFAULT_PRIORITIES);
  const statusFilter = parseFilter(searchParams.get('status'), STATUS_ORDER, DEFAULT_STATUSES);
  const printRef = useRef<HTMLDivElement>(null);

  // Print via a hidden iframe with the parent's stylesheets plus a print-friendly reset; cleaned up on `afterprint`.
  const handlePrint = () => {
    if (!printRef.current) return;
    const content = printRef.current.innerHTML;
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(n => `<link rel="stylesheet" href="${(n as HTMLLinkElement).href}">`)
      .join('\n');
    const styles = Array.from(document.querySelectorAll('style'))
      .map(n => `<style>${n.textContent ?? ''}</style>`)
      .join('\n');

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${workspaceName || 'Workspace'} - Document index</title>
    ${links}
    ${styles}
    <style>
      * { background: transparent !important; box-shadow: none !important; border-color: #D9E3E4 !important; }
      body { margin: 24px; color: #2F3A3D; }
    </style>
  </head>
  <body>${content}</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    iframe.srcdoc = html;
    let printed = false;
    iframe.addEventListener('load', () => {
      if (printed) return;
      printed = true;
      const w = iframe.contentWindow;
      if (!w) { iframe.remove(); return; }
      w.addEventListener('afterprint', () => iframe.remove());
      w.focus();
      w.print();
    });
    document.body.appendChild(iframe);
  };

  // Poll every 5s while jobs are active and the tab is visible; the final re-run pulls the settled state.
  useEffect(() => {
    const load = () =>
      authFetch('/workspace/index')
        .then(r => r.ok ? r.json() : [])
        .then(setSections)
        .catch(() => setSections([]));
    load();
    if (!hasActiveJobs) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 5_000);
    return () => clearInterval(interval);
  }, [hasActiveJobs]);

  // At least one option in each group stays ticked.
  const toggleFilter = <T extends string>(key: 'priority' | 'status', order: readonly T[], current: Set<T>, value: T) => {
    const next = new Set(current);
    if (next.has(value)) {
      if (next.size === 1) return;
      next.delete(value);
    } else next.add(value);
    const params = new URLSearchParams(searchParams);
    params.set(key, order.filter(o => next.has(o)).join(','));
    setSearchParams(params, { replace: true });
  };
  const togglePriority = (p: DocumentPriority) => toggleFilter('priority', PRIORITY_ORDER, priorityFilter, p);
  const toggleStatus = (s: DocStatus) => toggleFilter('status', STATUS_ORDER, statusFilter, s);

  if (sections === null) return <PageLoader label="Loading index..." />;

  const countLinked = (s: IndexSection | IndexSubsection): number => {
    const here = s.documents.reduce((n, d) => n + d.linked_files.length, 0);
    if ('subsections' in s) {
      return here + s.subsections.reduce((n, sub) => n + countLinked(sub), 0);
    }
    return here;
  };
  const totalLinked = sections.reduce((n, s) => n + countLinked(s), 0);

  const visibleSections = sections.filter(s => sectionMatchesFilter(s, priorityFilter, statusFilter));

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{workspaceName || 'Workspace'}</h3>
          <p className="text-sm text-coolslate mt-1">
            {totalLinked} document{totalLinked !== 1 ? 's' : ''} linked
          </p>
        </div>
      </div>

      <DismissableInfoCard
        storageKey="workspaceIndex.introDismissed"
        title="Build your company record"
      >
        Connect the apps and storage you already use and their documents are read into your company record and organised across the eight business areas.
      </DismissableInfoCard>

      <div className="mb-6">
        <ConnectSourceButton redirectTo={location.pathname + location.search} />
      </div>

      <div className="mb-6 empty:hidden"><NextSteps /></div>

      <div className="flex items-center w-full gap-1.5 p-1 mb-6 bg-offwhite border border-softgrey rounded-lg print:hidden">
        {PRIORITY_ORDER.map(p => {
          const checked = priorityFilter.has(p);
          const locked = checked && priorityFilter.size === 1;
          return (
            <label
              key={p}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-sm font-medium text-graphite select-none ${
                locked ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              title={locked ? 'At least one option must stay selected' : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={locked}
                onChange={() => togglePriority(p)}
                className="w-4 h-4 rounded border-softgrey text-brand focus:ring-brand cursor-pointer disabled:cursor-not-allowed"
              />
              {priorityStyles[p].label}
            </label>
          );
        })}
        <div className="w-px h-6 bg-softgrey mx-1" />
        {STATUS_ORDER.map(s => {
          const checked = statusFilter.has(s);
          const locked = checked && statusFilter.size === 1;
          return (
            <label
              key={s}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-sm font-medium text-graphite select-none ${
                locked ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              title={locked ? 'At least one option must stay selected' : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={locked}
                onChange={() => toggleStatus(s)}
                className="w-4 h-4 rounded border-softgrey text-brand focus:ring-brand cursor-pointer disabled:cursor-not-allowed"
              />
              {statusStyles[s].label}
            </label>
          );
        })}
        <div className="w-px h-6 bg-softgrey mx-1" />
        <button
          type="button"
          onClick={handlePrint}
          className="px-2 py-1.5 rounded-md text-coolslate hover:bg-white hover:text-graphite transition-colors"
          title="Print"
          aria-label="Print"
        >
          <Printer className="w-4 h-4" />
        </button>
      </div>

      <div ref={printRef}>
        {visibleSections.length === 0 ? (
          <div className="py-12 text-center">
            <List className="w-16 h-16 text-coolslate/70 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-graphite mb-2">No documents match the filter</h3>
            <p className="text-coolslate">Tick another option above to see more.</p>
          </div>
        ) : (
          <div>
            {visibleSections.map(section => (
              <SectionBlock
                key={section.id}
                section={section}
                priorityFilter={priorityFilter}
                statusFilter={statusFilter}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
